import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import { BudgetAddEdit } from '../components/BudgetAddEdit';
import { CSVImportOverlay } from '../components/CSVImportOverlay';
import { ImportRulesOverlay } from '../components/ImportRulesOverlay';
import { EOMOverlay } from '../components/EOMOverlay';
import { fetchBudgets, createBudget, updateBudget, Budget as BudgetType, getAllEOMReviews, EOMReview } from '../api/budgets';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export function Settings() {
  const [showBudgetOverlay, setShowBudgetOverlay] = useState(false);
  const [editBudget, setEditBudget] = useState<BudgetType | null>(null);
  const [budgets, setBudgets] = useState<BudgetType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCSVImportOverlay, setShowCSVImportOverlay] = useState(false);
  const [csvData, setCsvData] = useState('');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [showBudgetInfo, setShowBudgetInfo] = useState(false);
  const [showImportRulesOverlay, setShowImportRulesOverlay] = useState(false);
  const [eomReviews, setEomReviews] = useState<EOMReview[]>([]);
  const [loadingEOMReviews, setLoadingEOMReviews] = useState(false);
  const [showEOMOverlay, setShowEOMOverlay] = useState(false);
  const [selectedEOMYear, setSelectedEOMYear] = useState<number | null>(null);
  const [selectedEOMMonth, setSelectedEOMMonth] = useState<number | null>(null);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refreshBudgets = () => {
    setLoading(true);
    fetchBudgets()
      .then(data => {
        setBudgets(data);
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to load budgets');
        setLoading(false);
      });
  };

  useEffect(() => {
    refreshBudgets();
    loadEOMReviews();
  }, []);

  const loadEOMReviews = async () => {
    try {
      setLoadingEOMReviews(true);
      const reviews = await getAllEOMReviews();
      setEomReviews(reviews);
    } catch (err) {
      console.error('Failed to load EOM reviews:', err);
    } finally {
      setLoadingEOMReviews(false);
    }
  };

  const handleViewEOM = (year: number, month: number) => {
    setSelectedEOMYear(year);
    setSelectedEOMMonth(month);
    setShowEOMOverlay(true);
  };

  const handleEOMOverlayClose = () => {
    setShowEOMOverlay(false);
    setSelectedEOMYear(null);
    setSelectedEOMMonth(null);
    loadEOMReviews();
  };

  const handleSaveBudget = async (budget: { name: string; amount: number; icon: string; budgetType: 'Expense' | 'Savings' | 'Expense Fund'; savingsGoal?: 'Target Goal' | 'Monthly Goal'; targetDate?: string; expensePeriodValue?: number; expensePeriodUnit?: 'Months' | 'Years'; id?: string }) => {
    try {
      if (budget.id) {
        await updateBudget(budget.id, { 
          name: budget.name, 
          amount: budget.amount, 
          icon: budget.icon, 
          budgetType: budget.budgetType,
          savingsGoal: budget.savingsGoal,
          targetDate: budget.targetDate,
          expensePeriodValue: budget.expensePeriodValue,
          expensePeriodUnit: budget.expensePeriodUnit
        });
      } else {
        await createBudget({ 
          name: budget.name, 
          amount: budget.amount, 
          icon: budget.icon, 
          budgetType: budget.budgetType,
          savingsGoal: budget.savingsGoal,
          targetDate: budget.targetDate,
          expensePeriodValue: budget.expensePeriodValue,
          expensePeriodUnit: budget.expensePeriodUnit
        });
      }
      setShowBudgetOverlay(false);
      setEditBudget(null);
      refreshBudgets();
    } catch (err: any) {
      alert(err.message || 'Failed to save budget');
    }
  };

  const handleDeleteBudget = () => {
    refreshBudgets();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleImportTransactions = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if file is CSV
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (text) {
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length > 0) {
          const headerRow = lines[0].trim();
          const headers = headerRow.split(',').map(h => h.trim());

          setCsvData(text);
          setCsvHeaders(headers);
          setShowCSVImportOverlay(true);

          console.log('CSV Header Row:', headerRow);
          console.log('CSV Headers:', headers);
        }
      }
    };
    reader.readAsText(file);

    // Reset the input so the same file can be selected again if needed
    event.target.value = '';
  };

  const handleCSVImportClose = () => {
    setShowCSVImportOverlay(false);
    setCsvData('');
    setCsvHeaders([]);
  };

  const handleCSVImportConfirm = (mappings: { amount: string; datetime: string; note: string }) => {
    // Transition to reconciliation stage - don't close the overlay
    console.log('Confirmed CSV mappings:', mappings);
    // The overlay will handle the transition to the reconciliation stage
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-white">
      <div className="w-full max-w-4xl mx-auto flex flex-col min-h-screen h-screen">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between pt-4 pb-2 px-4 mb-4">
          <div className="flex-1 flex flex-col items-center">
            <img
              src="/budgie_logo_sm.png"
              alt="Budgie Logo"
              className="h-10 object-contain mx-auto"
              style={{ maxWidth: 100 }}
            />
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 px-6 pb-28 pt-6">
          {/* Budgets Section */}
          <div className="bg-[#F8F9FA] rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900">Budgets</h2>
                <button
                  onClick={() => setShowBudgetInfo(!showBudgetInfo)}
                  className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors border border-gray-300"
                >
                  <span className="text-gray-700 font-bold text-sm">?</span>
                </button>
              </div>
              <button
                className="p-2 rounded-lg bg-[#22C55E] text-white hover:bg-[#16A34A] transition-colors"
                onClick={() => { setShowBudgetOverlay(true); setEditBudget(null); }}
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>
            {loading ? (
              <div className="text-center text-gray-500 py-8">Loading...</div>
            ) : error ? (
              <div className="text-center text-red-500 py-8">{error}</div>
            ) : budgets.length === 0 ? (
              <div className="text-center text-gray-400 py-8">Create a budget above to view it here</div>
            ) : (
              <div className="max-h-80 overflow-y-auto space-y-3 pr-2">
                {budgets.map((budget) => (
                  <div key={budget.id} className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F8F9FA] mr-4">
                      <i className={`${budget.icon} text-sm`} aria-hidden="true"></i>
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{budget.name}</div>
                      <div className="text-sm text-gray-600">${budget.amount.toFixed(2)}</div>
                    </div>
                    <button
                      className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                      onClick={() => { setShowBudgetOverlay(true); setEditBudget(budget); }}
                    >
                      <PencilIcon className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Budget Info Dialog */}
          {showBudgetInfo && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col relative">
                <div className="flex-shrink-0 p-6 pb-4">
                  <div className="font-semibold text-lg text-gray-900 mb-4">Budget Creation Guide</div>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
                  <div className="text-sm text-gray-700">
                    <div className="space-y-4">
                    <div>
                      <div className="font-semibold text-base text-gray-900 mb-2">How to Create a Budget</div>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                          <div>Click the <span className="font-semibold text-green-600">+</span> button to add a new budget category</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                          <div>Choose a budget type: <strong>Expense</strong> for regular monthly spending, <strong>Expense Fund</strong> for saving toward future expenses, or <strong>Savings</strong> for financial goals</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                          <div>For <strong>Expense Funds</strong>, set the total amount needed, expense period (months or years), and period value. The app calculates your monthly savings goal automatically</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                          <div>For <strong>Savings</strong>, choose Monthly Goal (save a fixed amount each month) or Target Goal (save a total amount by a specific date). Set the amount and, for Target Goals, include a target date</div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="font-semibold text-base text-gray-900 mb-2">General Budgeting Tips</div>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                          <div><strong>Identify your monthly income:</strong> Start by knowing how much money you have available each month to allocate across all budget categories</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                          <div><strong>List consistent monthly expenses:</strong> Include essential costs like rent/mortgage, utility bills, food, vehicle payments, debt payments, insurance, and other recurring monthly obligations</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                          <div><strong>Identify Expense Funds:</strong> These are for expenses that occur less frequently than monthly. Examples include annual subscriptions, car maintenance, haircuts, new clothes, veterinarian visits, holiday gifts, or any expense you need to save for over time</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                          <div><strong>Set savings goals:</strong> Determine what you're saving for - emergency fund, vacation, down payment, retirement, or other financial objectives</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                          <div><strong>Be honest about spending:</strong> Every dollar of your income should be assigned to a budget category. Don't leave money unaccounted for</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                          <div><strong>Account for the little things:</strong> Be realistic about small expenses you might overlook - gifts, movie tickets, rentals, parking fees, miscellaneous shopping, coffee, snacks, and other incidental purchases</div>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowBudgetInfo(false)}
                  className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 bg-white rounded-full p-1 shadow-sm"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Divider */}
          <div className="mt-8 h-px bg-gray-200"></div>

          {/* Import Button */}
          <button
            className="mt-8 w-full py-3 rounded-full bg-[#22C55E] text-white text-lg font-semibold hover:bg-[#16A34A] transition"
            onClick={handleImportTransactions}
          >
            Import Transactions
          </button>

          {/* View Import Rules Button */}
          <button
            className="mt-4 w-full py-3 rounded-full bg-emerald-800 text-white text-lg font-semibold hover:bg-emerald-700 transition"
            onClick={() => setShowImportRulesOverlay(true)}
          >
            View Import Rules
          </button>

          {/* Divider */}
          <div className="mt-8 h-px bg-gray-200"></div>

          {/* EOM Reviews Section */}
          <div className="mt-8 bg-[#F8F9FA] rounded-xl p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">End of Month Reports</h2>
            {loadingEOMReviews ? (
              <div className="text-center text-gray-500 py-4">Loading...</div>
            ) : eomReviews.length === 0 ? (
              <div className="text-center text-gray-400 py-4">No EOM reports available</div>
            ) : (
              <div className="max-h-80 overflow-y-auto space-y-2 pr-2">
                {eomReviews.map((review) => {
                  const monthName = new Date(review.year, review.month).toLocaleString('default', { month: 'long', year: 'numeric' });
                  return (
                    <button
                      key={review._id}
                      onClick={() => handleViewEOM(review.year, review.month)}
                      className="w-full p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow text-left"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">{monthName}</div>
                          <div className="text-sm text-gray-500">
                            {review.reviewed 
                              ? `Reviewed on ${new Date(review.reviewedAt!).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                              : 'Not reviewed'}
                          </div>
                        </div>
                        <div className="text-gray-400">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="mt-8 h-px bg-gray-200"></div>

          {/* Logout Button */}
          <button
            className="mt-8 w-full py-3 rounded-full bg-gray-200 text-gray-700 text-lg font-semibold hover:bg-gray-300 transition"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Hidden file input for CSV import */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".csv"
        style={{ display: 'none' }}
      />

      <BudgetAddEdit
        open={showBudgetOverlay}
        onClose={() => { setShowBudgetOverlay(false); setEditBudget(null); }}
        onSave={handleSaveBudget}
        onDelete={handleDeleteBudget}
        initial={editBudget ? { 
          name: editBudget.name, 
          amount: editBudget.amount, 
          icon: editBudget.icon, 
          id: editBudget._id,
          budgetType: editBudget.budgetType,
          savingsGoal: editBudget.savingsGoal,
          targetDate: editBudget.targetDate,
          expensePeriodValue: editBudget.expensePeriodValue,
          expensePeriodUnit: editBudget.expensePeriodUnit
        } : undefined}
        isEdit={!!editBudget}
      />

      {showCSVImportOverlay && (
        <CSVImportOverlay
          csvData={csvData}
          csvHeaders={csvHeaders}
          onClose={handleCSVImportClose}
          onConfirmMapping={handleCSVImportConfirm}
        />
      )}

      {showImportRulesOverlay && (
        <ImportRulesOverlay
          onClose={() => setShowImportRulesOverlay(false)}
        />
      )}

      {showEOMOverlay && selectedEOMYear !== null && selectedEOMMonth !== null && (
        <EOMOverlay
          year={selectedEOMYear}
          month={selectedEOMMonth}
          onClose={handleEOMOverlayClose}
        />
      )}
    </div>
  );
} 